import type { HeroCopy } from './hero-en';
import LocaleLink from '@/components/LocaleLink';

/**
 * LOCKED 2026-05-25 — canvas/hero-lab.html lines 1056-1091 (TW_COPY.ar).
 * lead + sub revised 2026-08-31 per the copywriter review
 * (docs/plans/2026-08-31-copy-review/REVIEW.md, HERO rows): AR now mirrors
 * the EN facts (superseding the earlier "not a translation" voice lock for
 * these two strings), keeping all five OAuth-reviewer facts.
 *
 * sub revised again 2026-09-02, owner-supplied verbatim: scopes the input to
 * الاجتماعات الصوتية, replaces مخصصة لك with مخصصة حسب تفضيلك, and swaps the
 * indefinite تسجيلا صوتيا for the definite التسجيل الصوتي. All five OAuth
 * facts and the خصوصية وأمان highlight are unchanged.
 *
 * sub revised again 2026-09-07 (owner, marked up on a screenshot): مساحة عمل
 * becomes منصة عمل, and التسجيل الصوتي drops its للمحادثات والاجتماعات — those
 * two words already open the sentence, so the repetition was حشو of exactly the
 * kind the style guide bans. الصوتي stays, and carries the record-audio OAuth
 * fact on its own. The sub now sets two lines at 1440 instead of three.
 */
export const HERO_AR: HeroCopy = {
  lead: 'محادثاتك واجتماعاتك... مفرغة ومنظمة خلال ثوانٍ.',
  sub: (
    <>
      منصة عمل ذكية تفرّغ محادثاتك واجتماعاتك الصوتية إلى نصوص وملاحظات منظمة ومخصصة حسب
      تفضيلك، وتتيح لك التسجيل الصوتي وإدارة مواعيدك ومهامك بكل{' '}
      <LocaleLink to="/trust" className="mkt-hero-tw__sub-highlight">
        خصوصية وأمان
      </LocaleLink>
      .
    </>
  ),
  cta: 'ابدأ مجانًا',
  ctaNote: 'لا يتطلب بطاقة ائتمانية',
  /* ⚠️ AWAITING OWNER CONFIRMATION — these are proposals, not locked strings.
     Arabic labels on interactive surfaces need explicit sign-off before they
     ship (see the AR-labels reflex). Verb-first per docs/arabic-style-guide.md;
     The tab-4 noun deliberately matches the app's own nav label so the tab and
     the mockup below it agree — CHROME.ar.nav.actions in AppShell.tsx. Change
     one and you must change the other.

     2026-09-05: shifted to follow the EN order after transcription became tab 2.
     "يفرّغ" carries its shadda deliberately — undiacritized "يفرغ" reads as
     "empties", which is a real ambiguity and exactly the case the
     harakat-sparingly rule leaves room for. The noun tracks the EN change from
     the bilingual claim to the capability itself ("Audio to Text").

     2026-09-06: tab 3 tracks the EN replacement of "Saves / To Your Drive" with
     "Enables / Audio Recording". "يتيح" is the verb-first rendering of "enables";
     "تسجيل الصوت" matches the app's own recorder label (CHROME.ar.record uses
     "تسجيل"), so the tab and the mockup beneath it agree. ⚠️ PROPOSAL — needs
     owner confirmation like every other label here.

     2026-09-06 (b): tracks the EN swap of tabs 4 and 5. "يتابع" renders the new
     EN verb "Tracks" (متابعة, following an item through to completion) and
     replaces "يسنِد", which described assigning rather than tracking — the same
     correction the EN label made. ⚠️ PROPOSAL.

     2026-09-08: the noun goes "بنود الإجراءات" -> "المهام والإجراءات" (owner),
     renamed everywhere across the five tabs. The label moves WITH the app's nav
     item rather than being renamed on its own: the whole point of this noun is
     that the tab and the mockup underneath it say the same thing, and the demo
     now shows "المهام والإجراءات" in the sidebar, the page title, the minutes
     section heading and the template catalog. ⚠️ PROPOSAL.

     2026-09-07: tracks the EN replacement of "Schedules / Calendar Events" with
     "Tailors / Summaries & Templates". "يخصّص" is not a fresh coinage — it is
     the verb the SHIPPED hero sub already uses for this idea ("ومخصصة حسب
     تفضيلك"), so the tab and the paragraph above it say the same thing with the
     same word. It carries its shadda for the same reason "يفرّغ" does:
     undiacritized "يخصص" reads as the passive يُخَصَّص. ⚠️ PROPOSAL. */
  tabs: [
    { verb: 'ينشئ', noun: 'محاضر الاجتماعات', body: 'TODO_BODY_AR_1' },
    { verb: 'يفرّغ', noun: 'الصوت إلى نص', body: 'TODO_BODY_AR_2' },
    { verb: 'يتيح', noun: 'تسجيل الصوت', body: 'TODO_BODY_AR_3' },
    { verb: 'يتابع', noun: 'المهام والإجراءات', body: 'TODO_BODY_AR_4' },
    { verb: 'يخصّص', noun: 'الملخصات والقوالب', body: 'TODO_BODY_AR_5' },
  ],
  /* مصدر, per §3 and §14 — a button label takes the verbal noun rather than an
     imperative, which would force a gender on every reader. «السابقة» /
     «التالية» agree with «الميزة» (مؤنث).

     §13 is what makes these LOGICAL rather than physical: `navPrev` is the
     inline-START button, which renders at the RIGHT in Arabic. Writing «يمين»
     or «يسار» here would be correct in exactly one of the two locales, which is
     the failure the rule exists to prevent. ⚠️ PROPOSAL. */
  navPrev: 'الميزة السابقة',
  navNext: 'الميزة التالية',
};
